import { RiSparkling2Fill } from "react-icons/ri";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState, useRef } from "react";
import { Button, Typography, Box, Collapse } from "@mui/material";

import { SessionContext } from "@/utility/useSession";
import { formatDateTime } from "@/utility/utils";
import { api, RequestStatus } from "@/logic/api";


export const GenerateRecommendationsButton = () => {
  const [isPolling, setIsPolling] = useState(false);
  const [displayMessage, setDisplayMessage] = useState("");
  const [showDisplayMessage, setShowDisplayMessage] = useState(false);
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const generateRecommendationsApi = api.hooks.kmosV2.generateRecommendations();
  const getRecommendationRunStatusApi = api.hooks.kmosV2.getRecommendationRunStatus();
  const timeoutRef = useRef<number | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    getRecommendationRunStatusApi.send(session.current.context, {});
  }, []);

  useEffect(() => {
    if (generateRecommendationsApi.status === RequestStatus.loading) {
      setDisplayMessage("settings.generateRecommendations.loading");
    }

    if (generateRecommendationsApi.status === RequestStatus.error) {
      setDisplayMessage("settings.generateRecommendations.error");
    }

    if (generateRecommendationsApi.data?.accepted) {
      setDisplayMessage("settings.generateRecommendations.polling");
      startPolling();
    }
  }, [generateRecommendationsApi.status]);

  useEffect(() => {
    const data = getRecommendationRunStatusApi.data;
    if (!data) {
      return;
    }

    if (getRecommendationRunStatusApi.status === RequestStatus.error) {
      stopPolling();
      setDisplayMessage("settings.generateRecommendations.error");
      return;
    }

    if (!data.isRunning) {
      stopPolling();
      setDisplayMessage("settings.generateRecommendations.finished");
      return;
    }

    if (!isPolling) {
      setDisplayMessage("settings.generateRecommendations.polling");
      startPolling();
    }
  }, [getRecommendationRunStatusApi.data]);

  useEffect(() => {
    if (!displayMessage) {
      return;
    }

    setShowDisplayMessage(true);

    const persistentMessages = [
      "settings.generateRecommendations.finished",
      "settings.generateRecommendations.error",
    ];

    if (persistentMessages.includes(displayMessage)) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      if (!isPolling) {
        setShowDisplayMessage(false);
        timeoutRef.current = null;
      }
    }, 5000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [displayMessage, isPolling]);

  const startPolling = () => {
    if (pollIntervalRef.current) {
      return;
    }

    setIsPolling(true);
    pollIntervalRef.current = setInterval(() => {
      if (getRecommendationRunStatusApi.status !== RequestStatus.loading) {
        getRecommendationRunStatusApi.send(session.current.context, {});
      }
    }, 5000);
  };

  const stopPolling = () => {
    setIsPolling(false);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleClick = () => {
    generateRecommendationsApi.send(session.current.context, {})
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
      <Button
        startIcon={<RiSparkling2Fill />}
        loading={generateRecommendationsApi.status === RequestStatus.loading || isPolling}
        loadingPosition="start"
        variant="contained"
        sx={{
          background: `linear-gradient(135deg, #FD6585 30%, #FFD3A5 100%)`,
          backgroundSize: "120% 100%",
          backgroundPosition: "0 0",
          transition: "background-position 0.5s ease-in-out",
          color: "white",
          "&:hover": {
            backgroundPosition: "100% 0"
          },
          "&:disabled": {
            color: 'white'
          }
        }}
        onClick={handleClick}
      >
        {t("settings.generateRecommendations.defaultMessage")}
      </Button>
      <Collapse
        in={Boolean(getRecommendationRunStatusApi.data?.finishedAt && showDisplayMessage)}
        timeout="auto"
        unmountOnExit
      >
        <Typography variant="caption">
          {t(displayMessage, {
            finishedAt: getRecommendationRunStatusApi.data?.finishedAt ? formatDateTime(getRecommendationRunStatusApi.data?.finishedAt, session) : null
          })}
        </Typography>
      </Collapse>
    </Box>
  )
}
